
import React from 'react';
import { Link } from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faUserCircle,
    faCrown,
    faExchangeAlt,
    faCheck,
    faEdit,
    faLock,
    faUserTag,
    faUnlock,
    faShare
} from '@fortawesome/free-solid-svg-icons';

import { useTranslation } from 'react-i18next';

interface UserWelcomeProps {
    selectedParticipant: string;
    selectedParticipantId: string;
    eventId?: string;
    isEventClosed: boolean;
    copied: boolean;
    onSwitchUser: () => void;
    onShareEvent: () => void;
    onEditEvent: () => void;
    onCloseEvent: () => void;
    onReopenEvent: () => void;
    loggedInUser?: string | null;
    eventOwnerId: string;
}

const UserWelcome: React.FC<UserWelcomeProps> = ({
                                                     selectedParticipant,
                                                     selectedParticipantId,
                                                     eventId,
                                                     isEventClosed,
                                                     copied,
                                                     onSwitchUser,
                                                     onShareEvent,
                                                     onEditEvent,
                                                     onCloseEvent,
                                                     onReopenEvent,
                                                     loggedInUser,
                                                     eventOwnerId,
                                                 }) => {
    const { t } = useTranslation();
    const isLoggedIn = !!loggedInUser;
    const isActualOwner = loggedInUser === eventOwnerId;
    const isActingAsOwner = selectedParticipantId === eventOwnerId;
    const isActingAsOtherUser = isLoggedIn && loggedInUser !== selectedParticipantId;

    // Simplified role display
    const getRoleBadge = () => {
        if (isActingAsOwner) {
            return isActualOwner
                ? {
                    label: t('event.userWelcome.roles.owner'),
                    icon: faCrown,
                    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                }
                : {
                    label: t('event.userWelcome.roles.actingAsOwner'),
                    icon: faUserTag,
                    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                };
        }
        return isActingAsOtherUser
            ? {
                label: t('event.userWelcome.roles.actingAs'),
                icon: faUserTag,
                color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            }
            : {
                label: t('event.userWelcome.roles.participant'),
                icon: faUserCircle,
                color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
            };
    };

    const roleBadge = getRoleBadge();

    return (
        <div className="mb-8">
            <div
                className="glass-card p-6">
                <div
                    className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-3 lg:space-y-0">

                    {/* User Info - Simplified */}
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full">
                            <FontAwesomeIcon icon={faUserCircle} className="text-xl"/>
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <Link
                                    to={`/profile/${eventId}/${selectedParticipantId}`}
                                    className="text-lg font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                    {selectedParticipant}
                                </Link>
                                <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge.color}`}>
                                    <FontAwesomeIcon icon={roleBadge.icon} className="mr-1"/>
                                    {roleBadge.label}
                                </span>
                            </div>

                            {/* Only show this line if acting as someone else */}
                            {isActingAsOtherUser ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {t('event.userWelcome.messages.managingExpenses')}
                                </p>
                            ) : isActingAsOwner && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {t('event.userWelcome.messages.managingEvent')}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto">

                        <button
                            onClick={onShareEvent}
                            className="glass-btn-ghost flex items-center !px-2.5 sm:!px-3 !py-2 text-sm"
                        >
                            <FontAwesomeIcon icon={copied ? faCheck : faShare}
                                             className={`sm:mr-2 ${copied ? 'text-green-500' : ''}`}/>
                            <span className="hidden sm:inline">{copied ? t('event.userWelcome.buttons.copied') : t('event.userWelcome.buttons.share')}</span>
                        </button>

                        {isActualOwner && !isEventClosed && (
                            <button
                                onClick={onEditEvent}
                                className="glass-btn-ghost flex items-center !px-2.5 sm:!px-3 !py-2 text-sm !text-blue-600 dark:!text-blue-400"
                            >
                                <FontAwesomeIcon icon={faEdit} className="sm:mr-2"/>
                                <span className="hidden sm:inline">{t('event.userWelcome.buttons.edit')}</span>
                            </button>
                        )}

                        {isActualOwner && (
                            isEventClosed ? (
                                <button
                                    onClick={onReopenEvent}
                                    className="glass-btn-ghost flex items-center !px-2.5 sm:!px-3 !py-2 text-sm !text-green-600 dark:!text-green-400"
                                >
                                    <FontAwesomeIcon icon={faUnlock} className="sm:mr-2"/>
                                    <span className="hidden sm:inline">{t('event.userWelcome.buttons.active')}</span>
                                </button>
                            ) : (
                                <button
                                    onClick={onCloseEvent}
                                    className="glass-btn-ghost flex items-center !px-2.5 sm:!px-3 !py-2 text-sm !text-red-600 dark:!text-red-400"
                                >
                                    <FontAwesomeIcon icon={faLock} className="sm:mr-2"/>
                                    <span className="hidden sm:inline">{t('event.userWelcome.buttons.close')}</span>
                                </button>
                            )
                        )}

                        <button
                            onClick={onSwitchUser}
                            className="glass-btn-ghost flex items-center !px-2.5 sm:!px-3 !py-2 text-sm"
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} className="sm:mr-2"/>
                            <span className="hidden sm:inline">{t('event.userWelcome.buttons.switch')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserWelcome;